import { useFormContext } from 'react-hook-form';

import { Checkbox } from '@/components/ui/checkbox';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';

import {
  ACTIVATION_CHANNELS,
  ACTIVATION_CHANNEL_LABELS,
  type ActivatedUserPayload,
  type ActivationChannel,
  type CreateIncidentFormValues,
} from './createIncidentModel';

const CHANNEL_DESCRIPTIONS: Record<ActivationChannel, string> = {
  email: 'A link to confirm or decline the activation.',
  sms: 'A 160-character text with the same confirmation link.',
  voice: 'An automated call telling them to check email or text.',
  in_app: 'A Pratus notification for anyone already signed in.',
};

export function StepNotifications({
  activatedUsers,
  creatorUserGuid,
}: {
  activatedUsers: ActivatedUserPayload[];
  creatorUserGuid: string;
}) {
  const { watch, setValue } = useFormContext<CreateIncidentFormValues>();
  const channels = watch('activation_channels');
  const message = watch('activation_message');

  // The creator is active immediately, so they never get an activation request.
  const recipients = activatedUsers.filter(
    (user) => user.user_guid !== creatorUserGuid
  );

  const toggleChannel = (channel: ActivationChannel, next: boolean) => {
    const withoutChannel = channels.filter((entry) => entry !== channel);
    setValue(
      'activation_channels',
      next ? [...withoutChannel, channel] : withoutChannel,
      { shouldDirty: true }
    );
  };

  return (
    <div
      className="tw:mx-auto tw:max-w-3xl tw:space-y-8"
      data-tour="create-incident-notifications"
    >
      <div className="tw:space-y-3">
        <FieldLabel>Channels</FieldLabel>
        <FieldDescription>
          How everyone you activated is asked to confirm. Turn all of them off
          to create the incident without notifying anyone.
        </FieldDescription>
        <div className="tw:space-y-3">
          {ACTIVATION_CHANNELS.map((channel) => (
            <div key={channel} className="tw:flex tw:items-start tw:gap-2">
              <Checkbox
                id={`activation-channel-${channel}`}
                checked={channels.includes(channel)}
                onCheckedChange={(next) =>
                  toggleChannel(channel, next === true)
                }
                className="tw:mt-0.5"
              />
              <div className="tw:min-w-0 tw:flex-1">
                <Label
                  htmlFor={`activation-channel-${channel}`}
                  className="tw:text-sm tw:font-medium"
                >
                  {ACTIVATION_CHANNEL_LABELS[channel]}
                </Label>
                <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
                  {CHANNEL_DESCRIPTIONS[channel]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Field>
        <FieldLabel htmlFor="activation-message">Message</FieldLabel>
        <Textarea
          id="activation-message"
          rows={4}
          placeholder="Optional. e.g. Report to the EOC at 0600; bring your go-bag."
          value={message}
          onChange={(e) =>
            setValue('activation_message', e.target.value, {
              shouldDirty: true,
            })
          }
        />
        <FieldDescription>
          Added to each activation request alongside the confirmation link. Text
          messages are truncated to fit their 160-character limit.
        </FieldDescription>
      </Field>

      <div className="tw:space-y-2">
        <FieldLabel>Recipients ({recipients.length})</FieldLabel>
        <div className="tw:max-h-56 tw:overflow-y-auto tw:rounded-md tw:border tw:border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={2}
                    className="tw:text-xs tw:text-muted-foreground"
                  >
                    Nobody else is being activated, so no requests will be sent.
                  </TableCell>
                </TableRow>
              ) : (
                recipients.map((user) => (
                  <TableRow key={user.user_guid}>
                    <TableCell>{user.name}</TableCell>
                    <TableCell className="tw:text-muted-foreground">
                      {user.email_address}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
